import React from 'react'
import { VALUE_LEVEL_COLUMN } from './components/TalentAreasTable/model'
import { fetchCandidates } from 'api/candidates'
import { useModal, useLanguage } from 'hooks'
import {
  highValueLimit,
  mediumValueLimit,
} from 'components/shared/ScatterPlot/ScatterPlot'
import { capitalizeFirstLetter } from 'utils/stringFormat'
import { personalityOverviewTab } from 'components/modals/TestsReportModal/model'
import { TAB_NAMES } from './model'

const useModalQueries = () => {
  const language = useLanguage()

  const handleFetchCandidates = React.useCallback(
    async ({ id }) => await fetchCandidates({ id, query: { lang: language } }),
    [language],
  )

  return {
    handleFetchCandidates,
  }
}

const useFilterAndOrder = () => {
  const [ordering, setOrdering] = React.useState({
    ascending: null,
    descending: VALUE_LEVEL_COLUMN,
  })
  const [categoriesFilter, setCategoriesFilter] = React.useState()
  const [valueFilter, setValueFilter] = React.useState({
    highTalent: true,
    mediumTalent: true,
    lowTalent: true,
  })
  const setOrder = (direction, field) => {
    setOrdering({
      ascending: direction === 'ascending' ? field : null,
      descending: direction === 'descending' ? field : null,
    })
  }

  const setValue = (value, bool) => {
    setValueFilter({ ...valueFilter, [value]: bool })
  }

  const updateValueFilter = e => {
    const { name, checked } = e.target
    setValueFilter({ ...valueFilter, [name]: checked })
  }

  const updateCategoryFilter = e => {
    const { name, checked } = e.target
    setCategoriesFilter({ ...categoriesFilter, [name]: checked })
  }

  return {
    state: {
      ordering,
      categoriesFilter,
      valueFilter,
      updateValueFilter,
      updateCategoryFilter,
    },
    handlers: {
      setCategoriesFilter,
      setOrder,
      setValue,
    },
  }
}

export const useJobSeekerResultsModalState = state => {
  const { isOpen, jobSeeker } = state
  const { handleFetchCandidates } = useModalQueries()
  const language = useLanguage()
  const { isOpen: isGmaModalOpen, handleClose, handleOpen } = useModal()
  const [gmaScore, setGmaScore] = React.useState(null)
  const [showGma, setShowGma] = React.useState(false)
  const [showGmaToggle, setShowGmaToggle] = React.useState(false)
  const [talentData, setTalentData] = React.useState([])
  const [candidateResponse, setCandidateResponse] = React.useState(null)
  const [activeTab, setActiveTab] = React.useState(TAB_NAMES.OVERVIEW)
  const [personalityTab, setPersonalityTab] = React.useState(
    personalityOverviewTab().id,
  )
  const [selectedCandidates, setSelectedCandidates] = React.useState({})

  const {
    state: {
      ordering,
      categoriesFilter,
      valueFilter,
      updateValueFilter,
      updateCategoryFilter,
    },
    handlers: { setCategoriesFilter, setOrder, setValue },
  } = useFilterAndOrder()

  const handleTab = React.useCallback(tabId => {
    setActiveTab(tabId)
    setPersonalityTab(personalityOverviewTab().id)
  }, [])

  React.useEffect(() => {
    if (!jobSeeker?.id) {
      return
    }
    setSelectedCandidates({ [jobSeeker.id]: true })
  }, [jobSeeker?.id])

  React.useEffect(() => {
    if (!isOpen || !jobSeeker?.id) {
      return
    }
    setActiveTab(TAB_NAMES.OVERVIEW)
    setPersonalityTab(personalityOverviewTab().id)
  }, [isOpen, jobSeeker?.id])

  React.useEffect(() => {
    if (!isOpen) {
      setCandidateResponse(null)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen || !jobSeeker?.id) {
      return
    }
    let cancelled = false
    handleFetchCandidates({ id: jobSeeker.id }).then(result => {
      if (cancelled || !result?.response) {
        return
      }
      const response = result.response
      setCandidateResponse(response)
      setShowGmaToggle(!!response.talent_scores?.interest_integration_with_gma)
    })
    return () => {
      cancelled = true
    }
  }, [isOpen, jobSeeker?.id, handleFetchCandidates])

  React.useEffect(() => {
    if (!candidateResponse?.talent_scores) {
      return
    }
    const dataKey = showGma
      ? 'interest_integration_with_gma'
      : 'interest_integration'
    const talents = candidateResponse.talent_scores[dataKey]
    if (!talents) {
      return
    }

    const translatedCategories = talents.reduce((acc, { code, category }) => {
      return { ...acc, [code]: category }
    }, {})

    setTalentData(
      talents.map(talent => ({
        area: talent.name,
        personalityScore: Math.round(talent.percentile * 100),
        interestScore: Math.round(talent.interest_rescaled),
        matchScore: Math.round(talent.match),
        rawCategory: talent.category,
        description: talent.description,
        category:
          language === 'en'
            ? talent.category
            : translatedCategories[talent.code],
        code: talent.code,
        value: talent.match,
      })),
    )

    const categories = [
      ...new Set(
        talents.map(talent =>
          language === 'en'
            ? talent.category
            : translatedCategories[talent.code],
        ),
      ),
    ].reduce((acc, category) => {
      acc[capitalizeFirstLetter(category)] = true
      return acc
    }, {})

    setCategoriesFilter(categories)
  }, [candidateResponse, showGma, language])

  const filteredData = React.useMemo(() => {
    return talentData.filter(talent => {
      const totalScore = talent.matchScore
      const isHighValue = totalScore > highValueLimit
      const isMediumValue =
        totalScore > mediumValueLimit && totalScore <= highValueLimit
      const isLowValue = totalScore <= mediumValueLimit

      const valueFilterPass =
        (isHighValue && valueFilter.highTalent) ||
        (isMediumValue && valueFilter.mediumTalent) ||
        (isLowValue && valueFilter.lowTalent)

      const categoryFilterPass =
        categoriesFilter?.[capitalizeFirstLetter(talent.category)]

      return valueFilterPass && categoryFilterPass
    })
  }, [talentData, valueFilter, categoriesFilter])

  const orderedData = React.useMemo(() => {
    if (ordering.ascending) {
      return [...filteredData].sort((a, b) => {
        if (a[ordering.ascending] > b[ordering.ascending]) return 1
        if (a[ordering.ascending] < b[ordering.ascending]) return -1

        if (a[VALUE_LEVEL_COLUMN] > b[VALUE_LEVEL_COLUMN]) return 1
        if (a[VALUE_LEVEL_COLUMN] < b[VALUE_LEVEL_COLUMN]) return -1

        return 0
      })
    }
    if (ordering.descending) {
      return [...filteredData].sort((a, b) => {
        if (a[ordering.descending] < b[ordering.descending]) return 1
        if (a[ordering.descending] > b[ordering.descending]) return -1

        if (a[VALUE_LEVEL_COLUMN] < b[VALUE_LEVEL_COLUMN]) return 1
        if (a[VALUE_LEVEL_COLUMN] > b[VALUE_LEVEL_COLUMN]) return -1

        return 0
      })
    }
    return filteredData
  }, [filteredData, ordering])

  const handleConfirm = () => {
    handleClose()
  }

  return {
    state: {
      rawData: talentData,
      talentData: orderedData,
      ordering,
      categoriesFilter,
      valueFilter,
      isGmaOpen: isGmaModalOpen,
      gmaScore,
      showGma,
      showGmaToggle,
      language,
      candidateResponse,
      activeTab,
      personalityTab,
      selectedCandidates,
    },
    handlers: {
      setOrder,
      setValueFilter: setValue,
      updateValueFilter,
      updateCategoryFilter,
      handleOpen,
      handleClose,
      handleConfirm,
      setGmaScore,
      setShowGma,
      handleTab,
      setPersonalityTab,
      setSelectedCandidates,
    },
  }
}
